import { z } from 'zod';
// Re-export unified diagram types
export * from './diagram.js';
// Re-export container diagram types
export * from './container.js';
// Re-export schema diagram types
export * from './schema.js';
// Re-export pipeline diagram types
export * from './pipeline.js';
// Re-export API route diagram types
export * from './apiRoutes.js';
// Re-export dependency graph types
export * from './dependency.js';
// Re-export Phase 2 diagram types (AST-based)
export * from './callGraph.js';
export * from './sequence.js';
// Re-export E2E User Flow types (Issue #258)
export * from './userInteraction.js';
// Re-export API Call Tracer types (Issue #258 Phase 2)
export * from './apiCallTracer.js';
// Re-export Execution Path Tracer types (Issue #258 Phase 3)
export * from './executionPathTracer.js';
// Re-export Network Config Analyzer types (Issue #258 Phase 4)
export * from './networkConfigAnalyzer.js';
// Re-export E2E Flow Template types (Issue #258 Phase 6)
export * from './e2eFlowTemplate.js';
// Re-export E2E Flow Enhancement types (Issue #392)
export * from './e2eFlowEnhancement.js';
// Re-export E2E Flow Version types (Issue #415)
export * from './e2eFlowVersion.js';
// Re-export AI Verification types (Issue #411)
export * from './verification.js';
// Re-export Detection Pattern types (Issue #381)
export * from './detection.js';
// Re-export Generator interface types (Issue #666)
export * from './generator.js';
// Re-export Framework Plugin types (Issue #699)
export * from './frameworkPlugin.js';
// Re-export Frontend Call Graph types (Issue #803, epic #802)
export * from './frontendCallGraph.js';
// Re-export Backend Call Graph types (Issue #807, epic #802)
export * from './backendCallGraph.js';
// Re-export StitchSet types (Issue #810, epic #802)
export * from './stitchSet.js';
// Re-export dashboard summary types (Issue #819, Phase 1)
export * from './dashboardSummary.js';
// Re-export operational summary types (Issue #819, Phase 2)
export * from './operationalSummary.js';
// Re-export security summary types (Issue #819, Phase 3)
export * from './securitySummary.js';
// ============= User Types =============
export const UserSchema = z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string().nullable(),
    avatarUrl: z.string().url().nullable(),
    githubId: z.string(),
    createdAt: z.date(),
    updatedAt: z.date(),
});
// ============= Session Types =============
export const SessionSchema = z.object({
    id: z.string(),
    userId: z.string(),
    expiresAt: z.date(),
    createdAt: z.date(),
});
// ============= Project Types =============
export const ProjectSourceType = z.enum(['github', 'local']);
export const ProjectSchema = z.object({
    id: z.string(),
    name: z.string().min(1).max(255),
    description: z.string().nullable(),
    repoUrl: z.string().url().nullable(),
    localPath: z.string().nullable(),
    sourceType: ProjectSourceType.default('github'),
    createdAt: z.date(),
    updatedAt: z.date(),
    ownerId: z.string(),
    deletedAt: z.date().nullable().optional(), // Issue #159: Soft delete support
    excludePatterns: z.array(z.string()).default([]), // Issue #504: File exclusion glob patterns
    sources: z.array(z.lazy(() => SourceSchema)).optional(), // Issue #900/#902: Multi-source projects
});
export const CreateProjectSchema = z.object({
    name: z.string().min(1).max(255),
    description: z.string().optional(),
    repoUrl: z.string().url().optional(),
    localPath: z.string().optional(),
    sourceType: ProjectSourceType.optional().default('github'),
}).refine((data) => {
    // Require either repoUrl (for github) or localPath (for local)
    if (data.sourceType === 'github') {
        return !!data.repoUrl;
    }
    if (data.sourceType === 'local') {
        return !!data.localPath;
    }
    return true;
}, {
    message: 'GitHub projects require repoUrl, local projects require localPath',
});
export const UpdateProjectSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    description: z.string().optional(),
    repoUrl: z.string().url().optional(),
    localPath: z.string().optional(),
    sourceType: ProjectSourceType.optional(),
    excludePatterns: z.array(z.string()).optional(), // Issue #504
});
// ============= Source Types (Issue #900: Cross-repo analysis) =============
export const SourceRoleType = z.enum(['frontend', 'backend', 'shared', 'unknown']);
export const SourceSchema = z.object({
    id: z.string(),
    projectId: z.string(),
    label: z.string(),
    role: SourceRoleType,
    repoUrl: z.string().nullable(),
    localPath: z.string().nullable(),
    sourceType: ProjectSourceType,
    baseUrl: z.string().nullable(),
    createdAt: z.date().or(z.string()),
    updatedAt: z.date().or(z.string()),
});
export const CreateSourceSchema = z.object({
    label: z.string().min(1).max(100),
    role: SourceRoleType.default('unknown'),
    localPath: z.string().optional(),
    repoUrl: z.string().url().optional(),
    sourceType: ProjectSourceType.default('local'),
    baseUrl: z.string().optional(),
});
export const UpdateSourceSchema = z.object({
    label: z.string().min(1).max(100).optional(),
    role: SourceRoleType.optional(),
    baseUrl: z.string().nullable().optional(),
});
// ============= Diagram Types =============
export const DiagramNodeType = z.enum([
    'deployment',
    'codeBlock',
    'storage',
    'queue',
    'external',
    'database',
    'cache',
    'api',
    'frontend',
    'service',
    // Issue #414: Call graph node types for multi-handle support
    'function',
    'method',
    'arrow',
    'constructor',
    'entry',
    'critical',
    'hotspot',
    'callGraphNode',
]);
// Import CodeEvidenceSchema for DiagramNode evidence field
import { CodeEvidenceSchema } from './diagram.js';
export const DiagramNodeSchema = z.object({
    id: z.string(),
    type: DiagramNodeType,
    label: z.string(),
    description: z.string().optional(),
    position: z.object({
        x: z.number(),
        y: z.number(),
    }),
    data: z.record(z.unknown()).optional(),
    style: z.object({
        width: z.number().optional(),
        height: z.number().optional(),
        backgroundColor: z.string().optional(),
        borderColor: z.string().optional(),
    }).optional(),
    /** Code evidence supporting this node (Issue #203) */
    evidence: z.array(CodeEvidenceSchema).optional(),
});
export const DiagramEdgeSchema = z.object({
    id: z.string(),
    source: z.string(),
    target: z.string(),
    label: z.string().optional(),
    type: z.enum(['default', 'http', 'async', 'database', 'file']).optional(),
    animated: z.boolean().optional(),
    data: z.record(z.unknown()).optional(),
});
export const DiagramGraphSchema = z.object({
    nodes: z.array(DiagramNodeSchema),
    edges: z.array(DiagramEdgeSchema),
    // Issue #459: Optional call graph metadata for frontend display
    entryPoints: z.array(z.string()).optional(),
    leafFunctions: z.array(z.string()).optional(),
    criticalPaths: z.array(z.object({
        id: z.string(),
        path: z.array(z.string()),
        type: z.enum(['database', 'http', 'auth', 'file', 'queue', 'other']).optional(),
        isPrimary: z.boolean().optional(),
    })).optional(),
    hotspots: z.array(z.object({
        id: z.string(),
        callerCount: z.number(),
    })).optional(),
    unusedFunctions: z.array(z.string()).optional(),
});
export const DiagramLayoutSchema = z.object({
    zoom: z.number().default(1),
    position: z.object({
        x: z.number(),
        y: z.number(),
    }).default({ x: 0, y: 0 }),
    viewport: z.object({
        width: z.number(),
        height: z.number(),
    }).optional(),
});
export const DiagramSchema = z.object({
    id: z.string(),
    name: z.string().min(1).max(255),
    type: z.string().optional(), // Diagram type (e.g., 'errorHandling', 'callGraph')
    // #1034: `graph`/`layout` are omitted by the list endpoint (the graph
    // column is tens of MB on real projects). Single-diagram fetches via
    // GET /diagrams/:id still populate them. Callers that need element
    // counts from a list response should read `nodeCount`/`edgeCount`.
    graph: DiagramGraphSchema.optional(),
    layout: DiagramLayoutSchema.optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
    projectId: z.string(),
    // Issue #156: Diagram description metadata
    description: z.string().nullable().optional(),
    methodology: z.string().nullable().optional(),
    dataSources: z.any().nullable().optional(), // JSON field
    generatedBy: z.string().nullable().optional(),
    confidence: z.number().nullable().optional(),
    interpretationGuide: z.string().nullable().optional(),
    // #1034: persisted node/edge counts so the slim list endpoint can
    // surface them without loading `graph`. Existing pre-#1034 rows will
    // have nulls until re-analyzed.
    nodeCount: z.number().nullable().optional(),
    edgeCount: z.number().nullable().optional(),
    // `sourceFiles` removed from this schema — the top-level Diagram
    // column is never populated; the analyzer writes the list inside
    // graph.metadata.sourceFiles instead. Fetch the single diagram to
    // get it. (Followup from #1044 review.)
    // Issue #158: Diagram versioning and soft delete
    deletedAt: z.date().nullable().optional(),
    version: z.number().default(1),
    parentId: z.string().nullable().optional(),
    isLatest: z.boolean().default(true),
});
export const CreateDiagramSchema = z.object({
    name: z.string().min(1).max(255),
    projectId: z.string(),
    graph: DiagramGraphSchema.optional(),
    layout: DiagramLayoutSchema.optional(),
});
export const UpdateDiagramSchema = z.object({
    name: z.string().min(1).max(255).optional(),
    graph: DiagramGraphSchema.optional(),
    layout: DiagramLayoutSchema.optional(),
});
// ============= Analysis Types =============
export const JobStatusSchema = z.enum([
    'pending',
    'processing',
    'completed',
    'failed',
]);
export const AnalysisJobSchema = z.object({
    id: z.string(),
    status: JobStatusSchema,
    progress: z.number().min(0).max(100),
    stage: z.string().nullable().optional(), // Issue #373: Current pipeline stage
    result: z.unknown().nullable(),
    error: z.string().nullable(),
    projectId: z.string().optional(), // Optional for API responses
    createdAt: z.date().or(z.string()), // Allow string from API
    updatedAt: z.date().or(z.string()), // Allow string from API
});
// ============= GitHub Types =============
export const GitHubRepoInfoSchema = z.object({
    owner: z.string(),
    repo: z.string(),
    branch: z.string().optional(),
    path: z.string().optional(),
});
export const ParseGitHubUrlResultSchema = z.object({
    valid: z.boolean(),
    info: GitHubRepoInfoSchema.optional(),
    error: z.string().optional(),
});
// ============= API Response Types =============
export const ApiErrorSchema = z.object({
    error: z.string(),
    message: z.string(),
    statusCode: z.number(),
});
// ============= Export Format Types =============
export const ExportFormatSchema = z.enum(['svg', 'png', 'pdf']);
// `ExportOptionsSchema` and `ExportOptions` removed in #1074 B5 — only used by
// the deleted `POST /diagrams/:id/export` endpoint. Web-side `ExportOptions` /
// `ExtendedExportFormat` are defined locally in `exportUtils.ts` and the
// generator-side `ExportFormat` enum in `e2eFlowGenerator.ts` is unrelated.
//# sourceMappingURL=index.js.map